import {
  StandardDialog,
  StandardDialogFooter,
} from "../../../../pages/components/dialogs/standard-dialog/standard-dialog";
import React, { useState } from "react";
import Message from "../../../../pages/components/message";
import { MessageSections } from "../../../../pages/components/message/message";
import {
  InWarehouseActionTypes,
  ReadyForShipmentActionTypes,
} from "../../action-types.enum";
import ActionDialogOrderList from "../order-list/action-dialog-order-list";
import { OrderModel } from "../../../../pages/shared/models/order/order-item.model";
import ordersService from "../../../../services/orders.service";
import EmptyScanPopup from "./shared/empty";
import BarekamText from "../../../../pages/components/text";
import StateSelect from "./shared/state-select";
import {
  getOrders,
  getOrdersStatesSummary,
} from "../../../../redux/slices/orders-slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { setIsLoading } from "../../../../redux/slices/crm.slice";
import { OrderStatesEnum } from "../../../../pages/orders/enums/order-states.enum";
import { TrackNumberInput } from "./track-number-input";
import { ActionDialogContent } from "./shared/styled";
import { standardDialogColumns } from "./shared/columns";
import { actionsWithPreOpenedAdditionalDialogs } from "../../../../constants";
import { OrderGroupModel } from "../../../../pages/shared/models/order/orderGroupModel";

const BulkAction = ({
  actionType,
  preSelectedOrders,
  orderStateId,
  onCancel,
  isAdditionalPopupOpen,
  setIsAdditionalPopupOpen,
  ordersUpdated,
  groupData,
}: BulkActionDialogProps) => {
  const [buttonMessage] = useState<string>(getButtonMessage(actionType));

  const [orders, setOrders] = useState(
    preSelectedOrders || ([] as OrderModel[]),
  );
  const { ordersData } = useAppSelector((state) => state.ordersReducer);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { languageId } = useAppSelector((state) => state.messagesReducer);

  const dispatch = useAppDispatch();
  const preOpenDialog =
    actionsWithPreOpenedAdditionalDialogs.includes(actionType);

  function searchForOrder(trackingNumberToSearch: string) {
    if (
      !!trackingNumberToSearch.length &&
      !orders.some((order) =>
        order.products.some(
          (product) => product.trackingNumber === trackingNumberToSearch,
        ),
      )
    ) {
      const filter = decorateFilter(trackingNumberToSearch);

      if (filter) {
        ordersService
          .getOrderByGivenFilterCriteria(filter)
          .then((order) => {
            if (order) {
              const newOrders = [...orders, order];
              updateOrders(newOrders);
            }
          })
          .catch((reason) => {
            console.error(reason);
          });
      }
    }
  }

  function decorateFilter(trackingNumberToSearch: string) {
    switch (actionType) {
      case InWarehouseActionTypes.MARK_AS_READY_FOR_SHIPMENT:
        return {
          textQuery: trackingNumberToSearch,
          state: OrderStatesEnum.inWarehouse,
        };
      case InWarehouseActionTypes.READY_FOR_PICKUP:
        return {
          textQuery: trackingNumberToSearch,
          state: OrderStatesEnum.readyForPickup,
        };
      case ReadyForShipmentActionTypes.MARK_AS_SENT:
        return {
          textQuery: trackingNumberToSearch,
          state: OrderStatesEnum.readyForShipment,
        };
      case InWarehouseActionTypes.NOT_PREDEFINED:
      case InWarehouseActionTypes.CHANGE_STATUS:
        return {
          textQuery: trackingNumberToSearch,
        };
    }
    return null;
  }

  function updateOrders(orders: OrderModel[]) {
    setOrders(orders);
    ordersUpdated(orders);
  }

  function getButtonMessage(
    dialogType: InWarehouseActionTypes | ReadyForShipmentActionTypes,
  ) {
    switch (dialogType) {
      case InWarehouseActionTypes.MARK_AS_READY_FOR_SHIPMENT:
        return "mark_ready_for_shipment";
      case InWarehouseActionTypes.READY_FOR_PICKUP:
        return "mark_ready_for_pickup";
      default:
        return "change_statuses";
    }
  }

  function doAction(
    actionType: InWarehouseActionTypes | ReadyForShipmentActionTypes,
  ) {
    switch (actionType) {
      case InWarehouseActionTypes.MARK_AS_READY_FOR_SHIPMENT:
        onStateSelect(OrderStatesEnum.readyForShipment);
        break;
      case ReadyForShipmentActionTypes.MARK_AS_SENT:
        onStateSelect(OrderStatesEnum.onTheWay);
        break;
      case InWarehouseActionTypes.READY_FOR_PICKUP:
        onStateSelect(OrderStatesEnum.received);
        break;
      case InWarehouseActionTypes.NOT_PREDEFINED:
      case InWarehouseActionTypes.CHANGE_STATUS:
        setIsAdditionalPopupOpen && setIsAdditionalPopupOpen(true);
        break;
    }
  }

  function onStateSelect(orderStateId: number, warehouseId?: number) {
    const unpaidOrders = orders.find(
      (order) => order.paymentStatus?.paymentStatusId !== 2,
    );
    if (orderStateId === OrderStatesEnum.received && unpaidOrders) {
      setErrors({ unpaidOrders: "orders_must_be_paid" });
    } else {
      dispatch(setIsLoading(true));

      const orderIds = orders.map((order) => order.orderId) as number[];
      const updateData = {
        stateId: orderStateId,
      } as StateUpdateData;

      if (warehouseId) {
        updateData.intermediateWarehouse = warehouseId;
      }

      ordersService
        .bulkUpdate(orderIds, updateData, languageId)
        .then((response) => {
          dispatch(getOrders({ page: ordersData.currentPage || 0 }));
          dispatch(getOrdersStatesSummary());

          setIsAdditionalPopupOpen && setIsAdditionalPopupOpen(false);

          dispatch(setIsLoading(false));
          onCancel();
        })
        .catch((reason) => {
          console.log(reason);
          dispatch(setIsLoading(false));
        });
    }
  }

  const AdditionalDialog = () => {
    switch (actionType) {
      case InWarehouseActionTypes.NOT_PREDEFINED:
      case InWarehouseActionTypes.CHANGE_STATUS:
        return (
          <StandardDialog
            headerLabel={
              <BarekamText variant={"l"} bold>
                <Message
                  messageKey={"change_status"}
                  sectionId={MessageSections.ORDERS}
                />
              </BarekamText>
            }
            width={"sm"}
            open={!!isAdditionalPopupOpen}
          >
            <StateSelect
              orderStateId={orderStateId}
              onSave={onStateSelect}
              groupData={groupData}
              error={errors}
              onCancel={() => {
                if (preOpenDialog) {
                  onCancel();
                } else {
                  setIsAdditionalPopupOpen && setIsAdditionalPopupOpen(false);
                }
              }}
            />
          </StandardDialog>
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <ActionDialogContent>
        <TrackNumberInput onEnter={searchForOrder} />

        {orders.length ? (
          <ActionDialogOrderList
            orders={orders}
            columns={standardDialogColumns}
            updateOrders={updateOrders}
          />
        ) : (
          <EmptyScanPopup />
        )}
      </ActionDialogContent>

      <StandardDialogFooter
        saveLabel={
          orders.length ? (
            <Message
              messageKey={buttonMessage}
              sectionId={MessageSections.ORDERS}
            />
          ) : undefined
        }
        exportableOrders={orders}
        onSave={() => doAction(actionType)}
        onCancel={() => onCancel()}
      />

      <AdditionalDialog />
    </>
  );
};

interface BulkActionDialogProps {
  actionType: InWarehouseActionTypes | ReadyForShipmentActionTypes;
  onCancel: () => void;
  orderStateId?: number | string;
  preSelectedOrders?: OrderModel[];
  isAdditionalPopupOpen?: boolean;
  setIsAdditionalPopupOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  ordersUpdated: (orders: OrderModel[]) => void;
  groupData?: OrderGroupModel;
}

interface StateUpdateData {
  stateId: number;
  intermediateWarehouse?: number;
}

export default BulkAction;
